import { cn } from "@/lib/utils";

/**
 * The Trialion logo: the isometric T and the TRIALION wordmark.
 *
 * The mark is a T drawn on a 30° isometric grid, with a diamond off the end
 * of the crossbar, a diamond under the stem and a small triangle tucked
 * beside it. It is redrawn here as vectors from the supplied artwork and
 * snapped to a true 30° slope, so every sloped edge is parallel and it stays
 * sharp at any size.
 *
 * It is drawn in a SINGLE colour. Brand Guidelines vol. 01, 01 · The Mark:
 * "It is always drawn whole, at 30°, in a single color", and 01 · Misuse
 * forbids shadows, bevels and effects. An earlier revision gave each face its
 * own tone so the form read as lit from above; that is now retired, and the
 * `flat` prop is kept only so existing call sites keep working.
 *
 * The wordmark is DM Sans Bold in capitals, TRIAL upright and ION in italic.
 * That construction is fixed; only the three colours change, and they change
 * as a set — one of the numbered formats from the lockup sheet, chosen by the
 * ground. globals.css holds them as --lk-mark / --lk-trial / --lk-ion:
 *   07 · Flare accent  THE PRIMARY — Flare mark and ION, Eclipse TRIAL
 *   06 · Light master  Aura on light, for small sizes (.lk-light)
 *   01 · Signature     gold on Eclipse, for dark grounds (.lk-signature)
 *   10 · Knockout      mono Halo, for photography (.lk-knockout)
 *   08 · Mono Eclipse  one ink, for print and documents (.lk-mono)
 * The gold is never used on a light ground: it measures 2.0:1 on Halo.
 *
 * The same geometry is exported as artwork in /public:
 *   trialion-mark.svg / .png       the mark, gold on transparent
 *   trialion-icon.svg              gold on an Eclipse tile, the favicon
 *   favicon-48.png, apple-icon.png the same tile as PNG
 *   trialion-logo-master.png       2000 px master
 */

const T_FACE = "M17.5 0L74.94 33.16L74.94 53.51L55.2 42.11L55.2 88.45L37.25 78.08L37.25 31.74L17.5 20.34Z";
const DIAMONDS = ["M0 31.86L16.16 22.53L32.31 31.86L16.16 41.19Z", "M19.9 90.5L36.35 81L52.8 90.5L36.35 100Z"];
const SIDE = "M57.89 47.12L72.79 55.72L57.89 64.32Z";

/**
 * The mark on its own, for compact slots where the wordmark won't fit.
 *
 * The fill comes from --lk-mark so the mark takes whichever lockup format its
 * ground has set, and falls back to the gold when nothing sets it — which is
 * what a standalone mark wants, since it is only ever placed on Eclipse.
 *
 * `flat` is vestigial: it once selected a per-face gradient treatment, which
 * the brand book retired. Both renderings have been identical since, so it is
 * accepted and ignored rather than kept as two copies of one branch.
 */
export function TrialionMark({ className }: { className?: string; flat?: boolean }) {
  return (
    <svg
      viewBox="0 0 74.94 100"
      aria-hidden
      focusable={false}
      className={cn("h-8 w-auto fill-[var(--lk-mark,#d5a665)]", className)}
    >
      {[T_FACE, ...DIAMONDS, SIDE].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/** The full lockup. `tagline` adds the strapline beneath it (footer). */
export function BrandLogo({
  className,
  tagline = false,
}: {
  className?: string;
  tagline?: boolean;
}) {
  const lockup = (
    <span
      role="img"
      aria-label="Trialion"
      className="brand-lockup inline-flex items-end transition-transform group-hover:scale-[1.02]"
      data-size={tagline ? "lg" : "md"}
    >
      <TrialionMark className="brand-lockup-mark" />
      <span className="brand-lockup-word" aria-hidden="true">
        TRIAL<i>ION</i>
      </span>
    </span>
  );

  if (!tagline) return <span className={cn("inline-flex", className)}>{lockup}</span>;

  return (
    /* items-center so the strapline sits centred under the lockup rather than
       ragged left against a wider logo. */
    <span className={cn("inline-flex flex-col items-center gap-2.5", className)}>
      {lockup}
      <span className="text-[11px] font-medium tracking-[0.14em] text-muted-foreground">
        INTELLIGENCE IN EVERY PHASE
      </span>
    </span>
  );
}
